import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, Save, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const Q_TYPES = [
    { value: "echelle_1_5", label: "Échelle 1-5" },
    { value: "qcm_single", label: "QCM (un choix)" },
    { value: "qcm_multi", label: "QCM (multiple)" },
    { value: "oui_non", label: "Oui / Non" },
    { value: "text_libre", label: "Texte libre" },
];

const TITLE = {
    pre: "Évaluation pré-formation",
    post_acquis: "Évaluation des acquis",
    satisfaction_chaud: "Satisfaction à chaud",
};

export default function EvaluationEditor() {
    const { formation_slug, eval_type } = useParams();
    const navigate = useNavigate();
    const [tpl, setTpl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(
                    `/formateur/formations/${formation_slug}/evaluations/${eval_type}`
                );
                setTpl({
                    title: data.title || "",
                    description: data.description || "",
                    questions: (data.questions || []).map((q) => ({ ...q })),
                });
            } catch (err) {
                toast.error(formatApiError(err));
                navigate("/formateur/evaluations");
            } finally {
                setLoading(false);
            }
        })();
    }, [formation_slug, eval_type, navigate]);

    const updateQ = (idx, patch) => {
        setTpl((t) => ({
            ...t,
            questions: t.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
        }));
    };

    const addQ = () => {
        setTpl((t) => ({
            ...t,
            questions: [
                ...t.questions,
                {
                    id: `new-${Date.now()}`,
                    text: "",
                    type: "echelle_1_5",
                    options: [],
                    required: true,
                    weight: 0,
                },
            ],
        }));
    };

    const removeQ = (idx) => {
        setTpl((t) => ({ ...t, questions: t.questions.filter((_, i) => i !== idx) }));
    };

    const move = (idx, dir) => {
        setTpl((t) => {
            const next = [...t.questions];
            const target = idx + dir;
            if (target < 0 || target >= next.length) return t;
            [next[idx], next[target]] = [next[target], next[idx]];
            return { ...t, questions: next };
        });
    };

    const save = async () => {
        if (!tpl.title.trim()) {
            toast.error("Le titre est obligatoire.");
            return;
        }
        if (tpl.questions.length === 0) {
            toast.error("Ajoutez au moins une question.");
            return;
        }
        if (tpl.questions.some((q) => !q.text.trim())) {
            toast.error("Toutes les questions doivent avoir un libellé.");
            return;
        }
        setSaving(true);
        try {
            await api.put(
                `/formateur/formations/${formation_slug}/evaluations/${eval_type}`,
                {
                    title: tpl.title,
                    description: tpl.description,
                    questions: tpl.questions.map((q) => ({
                        id: q.id.startsWith("new-") ? undefined : q.id,
                        text: q.text,
                        type: q.type,
                        options: q.options || [],
                        required: !!q.required,
                        weight: Number(q.weight) || 0,
                    })),
                }
            );
            toast.success("Questionnaire enregistré");
            navigate("/formateur/evaluations");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    if (loading || !tpl) {
        return <div className="mx-auto max-w-4xl px-6 py-16 text-center text-brand-smoke">Chargement…</div>;
    }

    return (
        <div className="mx-auto max-w-4xl px-6 py-10" data-testid="page-evaluation-editor">
            <Link to="/formateur/evaluations" className="inline-flex items-center gap-2 text-sm text-brand-smoke hover:text-brand-ink">
                <ArrowLeft className="h-4 w-4" /> Retour aux évaluations
            </Link>
            <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-brand-orange">
                {TITLE[eval_type]}
            </div>
            <h1 className="mt-2 font-display text-4xl font-normal tracking-tightest">{formation_slug}</h1>

            <div className="mt-8 space-y-5 rounded-2xl border border-brand-line bg-white p-6 shadow-premium">
                <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Titre</label>
                    <input
                        value={tpl.title}
                        onChange={(e) => setTpl((t) => ({ ...t, title: e.target.value }))}
                        data-testid="eval-title"
                        className="mt-2 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                    />
                </div>
                <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-brand-smoke">Description</label>
                    <textarea
                        rows={3}
                        value={tpl.description}
                        onChange={(e) => setTpl((t) => ({ ...t, description: e.target.value }))}
                        data-testid="eval-description"
                        className="mt-2 w-full resize-none rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                    />
                </div>
            </div>

            <div className="mt-6 flex items-center justify-between">
                <h2 className="font-display text-2xl">Questions ({tpl.questions.length})</h2>
                <button
                    onClick={addQ}
                    data-testid="eval-add-question"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                >
                    <Plus className="h-3.5 w-3.5" /> Ajouter une question
                </button>
            </div>

            <div className="mt-4 space-y-3">
                {tpl.questions.map((q, idx) => (
                    <div key={q.id} className="rounded-2xl border border-brand-line bg-white p-5" data-testid={`eval-q-${idx}`}>
                        <div className="flex items-start gap-3">
                            <div className="flex flex-col items-center gap-1 pt-2 text-brand-smoke">
                                <button onClick={() => move(idx, -1)} className="text-xs hover:text-brand-orange">↑</button>
                                <GripVertical className="h-4 w-4" />
                                <button onClick={() => move(idx, 1)} className="text-xs hover:text-brand-orange">↓</button>
                            </div>
                            <div className="flex-1 space-y-3">
                                <div>
                                    <label className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Question {idx + 1}</label>
                                    <input
                                        value={q.text}
                                        onChange={(e) => updateQ(idx, { text: e.target.value })}
                                        data-testid={`eval-q-text-${idx}`}
                                        placeholder="Libellé de la question"
                                        className="mt-1 w-full rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                    <div>
                                        <label className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Type</label>
                                        <select
                                            value={q.type}
                                            onChange={(e) => updateQ(idx, { type: e.target.value })}
                                            data-testid={`eval-q-type-${idx}`}
                                            className="mt-1 w-full rounded-xl border border-brand-line px-3 py-2.5 outline-none focus:border-brand-orange"
                                        >
                                            {Q_TYPES.map((qt) => (
                                                <option key={qt.value} value={qt.value}>{qt.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Poids (scoring)</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={100}
                                            value={q.weight}
                                            onChange={(e) => updateQ(idx, { weight: e.target.value })}
                                            className="mt-1 w-full rounded-xl border border-brand-line px-3 py-2.5 outline-none focus:border-brand-orange"
                                        />
                                    </div>
                                    <label className="flex items-end gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={q.required}
                                            onChange={(e) => updateQ(idx, { required: e.target.checked })}
                                        />
                                        Obligatoire
                                    </label>
                                </div>
                                {(q.type === "qcm_single" || q.type === "qcm_multi") && (
                                    <div>
                                        <label className="text-[10px] font-semibold uppercase tracking-wider text-brand-smoke">Options (une par ligne)</label>
                                        <textarea
                                            rows={3}
                                            value={(q.options || []).join("\n")}
                                            onChange={(e) => updateQ(idx, { options: e.target.value.split("\n").filter(Boolean) })}
                                            className="mt-1 w-full resize-none rounded-xl border border-brand-line px-4 py-2.5 outline-none focus:border-brand-orange"
                                        />
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => removeQ(idx)}
                                data-testid={`eval-q-delete-${idx}`}
                                className="text-brand-smoke hover:text-red-600"
                                title="Supprimer"
                            >
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-8 flex items-center justify-end gap-3">
                <Link
                    to="/formateur/evaluations"
                    className="rounded-full border border-brand-line bg-white px-6 py-3 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                >
                    Annuler
                </Link>
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="eval-save"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                >
                    <Save className="h-3.5 w-3.5" />
                    {saving ? "Enregistrement…" : "Enregistrer le questionnaire"}
                </button>
            </div>
        </div>
    );
}
